import React from 'react';
import DynamicChart from './DynamicChart';
import DashboardComponentCreator from './DashboardComponentCreator';

// 스타일 객체를 컴포넌트 외부에 상수로 정의 (StreamingResponse와 동일)
const chartStyle = {
  padding: '10px'
};

/**
 * 단일 채팅 메시지를 렌더링하는 컴포넌트 (블록 기반 렌더링 지원)
 */
const ChatMessage = React.memo(({ msg, formatMarkdownLike, isDeveloperMode, onImageClick }) => {
  if (!msg || !msg.role) return null;
  
  const isUser = msg.role === 'user';
  const isSystem = msg.role === 'system';
  
  // 시스템 메시지 처리
  if (isSystem) {
    const systemClass = msg.cancelled ? 'message-system cancelled' : 'message-system';
    return (
      <div className={systemClass}>
        {msg.content}
        {msg.timestamp && isDeveloperMode && (
          <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.7 }}>
            {new Date(msg.timestamp).toLocaleTimeString()}
          </div>
        )}
      </div>
    );
  }
  
  const messageClass = isUser ? 'user-message' : 'assistant-message';
  
  // 중단된 AI 메시지인 경우 cancelled 클래스 추가
  const finalMessageClass = msg.cancelled && !isUser ? `${messageClass} cancelled` : messageClass;

  // AI 메시지 블록 렌더링 함수
  const renderAiMessageBlocks = () => {
    // msg.blocks가 없거나 배열이 아니면 기존 방식 또는 빈 내용 반환
    if (!Array.isArray(msg.blocks)) {
      // 기존 content 필드가 있다면 그것을 렌더링 (하위 호환성)
      if (msg.content) {
         return (
           <div 
             className="markdown-content prose compact-prose"
             dangerouslySetInnerHTML={{ __html: formatMarkdownLike(msg.content || "", isDeveloperMode) }}
           />
         );
      }
      return null; // 또는 기본 메시지
    }
    
    return msg.blocks.map((block, index) => {
      // Apply custom spacer class conditionally
      const spacerClass = index > 0 ? 'chat-block-spacer' : '';
      
      if (block.type === 'text') {
        return (
          <div 
            key={`block-${index}`}
            // Apply custom spacer class
            className={`markdown-content prose compact-prose text-block ${spacerClass}`}
            dangerouslySetInnerHTML={{ __html: formatMarkdownLike(block.content || "", isDeveloperMode) }}
          />
        );
      } else if (block.type === 'visualization') {
        return (
          // Apply custom spacer class, remove my-4
          <div key={block.id || `block-${index}`} className={`visualization-container chat-visualization-block ${spacerClass}`}>
            <DynamicChart
              chartData={block.data}
              style={block.data?.viz_type === 'webframe' ? {} : chartStyle}
              debugMode={isDeveloperMode}
            />
            {/* 개발자 모드일 때 원본 시각화 데이터도 표시 */}
            {isDeveloperMode && (
              <div className="developer-metadata-block" style={{
                marginTop: '15px',
                padding: '12px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                fontSize: '12px',
                fontFamily: 'Monaco, Consolas, "Courier New", monospace'
              }}>
                <div style={{
                  fontWeight: 'bold',
                  marginBottom: '8px',
                  color: '#495057',
                  fontSize: '13px'
                }}>
                  🔧 시각화 데이터 (개발자 모드)
                </div>
                <pre style={{
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  color: '#212529',
                  backgroundColor: '#ffffff',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #dee2e6',
                  maxHeight: '300px',
                  overflow: 'auto'
                }}>
                  {JSON.stringify(block.data, null, 2)}
                </pre>
              </div>
            )}
          </div>
        );
      } else if (block.type === 'dashboard_component') {
        // 대시보드 컴포넌트는 스트리밍 중에만 처리하고, 채팅 히스토리에서는 단순 메시지로 표시
        return (
          <div key={block.id || `block-${index}`} className={`dashboard-component-message chat-dashboard-component-message ${spacerClass}`}>
            <div className="component-creation-summary">
              <span className="success-icon">✅</span>
              <span>대시보드 컴포넌트 "{block.data?.component_template?.title || '새 컴포넌트'}"가 추가되었습니다.</span>
            </div>
          </div>
        );
      } else if (block.type === 'warning') {
        return (
          <div 
            key={`block-${index}`}
            // 경고 스타일 추가
            className={`warning-block prose compact-prose chat-warning-block ${spacerClass}`}
          >
            <span className="warning-icon">⚠️</span> {/* 경고 아이콘 */}
            <span 
              dangerouslySetInnerHTML={{ 
                __html: formatMarkdownLike(block.content || "", isDeveloperMode)
              }}
            />
          </div>
        );
      }
    });
  };

  return (
    <div className={`message ${finalMessageClass}`}>
      <div className="message-header">
        <span className="message-sender">{isUser ? '사용자' : 'AI 어시스턴트'}</span>
      </div>
      <div className="message-content">
        {isUser ? (
          <>
            {/* 이미지 URL을 포함한 텍스트 처리 */}
            {(() => {
              // 메시지에서 이미지 URL 파싱
              const imagePattern = /\[첨부된 이미지\]\n(\/media\/chat_images\/[^\s]+)/g;
              const matches = [...(msg.content || '').matchAll(imagePattern)];
              const imageUrls = matches.map(match => match[1]);
              
              // 이미지 URL을 제거한 텍스트
              const textWithoutImages = (msg.content || '').replace(imagePattern, '').trim();
              
              return (
                <>
                  {textWithoutImages && <div className="message-text">{textWithoutImages}</div>}
                  
                  {/* msg.images가 있는 경우 (새로운 방식) */}
                  {msg.images && msg.images.length > 0 && (
                    <div className="message-images">
                      {msg.images.map((img, index) => (
                        <div key={index} className="message-image-container">
                          <img 
                            src={img.file ? URL.createObjectURL(img.file) : img.url} 
                            alt={img.filename || `이미지 ${index + 1}`}
                            className="message-image"
                            onClick={() => onImageClick && onImageClick(img)}
                            style={{ cursor: 'pointer' }}
                            onError={(e) => {
                              // 이미지 로드 실패 시 URL로 다시 시도
                              if (img.file) {
                                e.target.src = img.url;
                              }
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* 텍스트에서 파싱한 이미지 URL이 있는 경우 */}
                  {imageUrls.length > 0 && !msg.images && (
                    <div className="message-images">
                      {imageUrls.map((url, index) => (
                        <div key={index} className="message-image-container">
                          <img 
                            src={url} 
                            alt={`이미지 ${index + 1}`}
                            className="message-image"
                            onClick={() => onImageClick && onImageClick({ url, filename: `이미지 ${index + 1}` })}
                            style={{ cursor: 'pointer' }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </>
        ) : (
          // AI 메시지는 블록 렌더링 함수 사용
          renderAiMessageBlocks()
        )}
      </div>
    </div>
  );
});

export default ChatMessage; 